/**
 * RFC 7807 problem-details shape returned by the AMI API for error responses.
 * Per `.claude/rules/coding-standards.md`, the back end emits
 * `application/problem+json` for 4xx/5xx; the SPA narrows responses to this
 * shape so callers can react to typed fields rather than parsing strings.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  errors?: Record<string, string[]>;
  [key: string]: unknown;
}

/**
 * Thrown by the axios response interceptor when the API returns a
 * `problem+json` payload. Surfaces the typed problem alongside the HTTP
 * status so UI code can branch on validation vs. authorization vs. server
 * errors without re-parsing the response body.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(status: number, problem: ProblemDetails) {
    super(problem.title ?? problem.detail ?? `API error (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }
}

/**
 * Extracts the stable machine-readable error code from a problem response. The
 * backend encodes it as the last segment of `type`
 * (`https://…/problems/{code}`) rather than a discrete field, so callers that
 * branch on a specific code (e.g. `site_user.email_in_use`) read it from here
 * instead of a non-existent `problem.code`. Returns `undefined` for non-API
 * errors or a typeless problem.
 */
export function problemCode(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const { type } = err.problem;
  if (typeof type !== 'string' || type.length === 0) return undefined;
  const segment = type.split('/').pop();
  return segment && segment.length > 0 ? segment : undefined;
}

export function isProblemDetails(value: unknown): value is ProblemDetails {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.type === 'string' ||
    typeof candidate.title === 'string' ||
    typeof candidate.status === 'number'
  );
}
